import React, { useState, useEffect } from 'react';
import { useToast } from '../Toast';
import Icon from '../Icon';
import { C, S } from '../../utils/styles';

export default function TaxSyncPreviewModal({ onClose, onApplied }) {
  const addToast = useToast();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [filter, setFilter] = useState('all'); // all | added | updated | removed

  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/tax-api/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      } else {
        const error = await response.json();
        addToast(error.detail || 'Failed to fetch preview', 'error');
        onClose();
      }
    } catch (err) {
      console.error('Failed to fetch preview:', err);
      addToast('Failed to fetch tax rate preview', 'error');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!confirmed) {
      addToast('Please confirm you have reviewed the changes', 'warning');
      return;
    }

    setApplying(true);
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/tax-api/apply/${preview.snapshotId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        addToast(`✓ ${result.message} (Snapshot: ${result.snapshotId})`, 'success');
        onApplied();
      } else {
        const error = await response.json();
        addToast(error.detail || 'Failed to apply changes', 'error');
      }
    } catch (err) {
      console.error('Failed to apply sync:', err);
      addToast('Failed to apply tax rate changes', 'error');
    } finally {
      setApplying(false);
    }
  };

  const filteredChanges = preview?.changes.filter(change => {
    if (filter === 'all') return true;
    return change.changeType === filter;
  }) || [];

  const changeTypeBadge = (type) => {
    const badges = {
      added: { bg: 'rgb(220, 252, 231)', color: 'rgb(22, 163, 74)', text: 'Added' },
      updated: { bg: 'rgb(224, 242, 254)', color: 'rgb(2, 132, 199)', text: 'Updated' },
      removed: { bg: 'rgb(254, 226, 226)', color: 'rgb(220, 38, 38)', text: 'Removed' },
      skipped: { bg: 'rgb(241, 245, 249)', color: 'rgb(100, 116, 139)', text: 'Skipped (No API Data)' }
    };
    const badge = badges[type] || badges.updated;

    return (
      <span style={{
        padding: '3px 8px',
        borderRadius: 6,
        background: badge.bg,
        color: badge.color,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {badge.text}
      </span>
    );
  };

  return (
    <div
      data-testid="tax-sync-preview-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'white',
        borderRadius: 14,
        boxShadow: '0 10px 30px rgba(2, 6, 23, 0.15)',
        maxWidth: 1200,
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ ...S.heading, margin: 0 }}>Tax Rate Sync Preview</h2>
            {preview && (
              <p style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {preview.source} • Effective {preview.effectiveDate}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              padding: 8,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: C.textMid,
              borderRadius: 8
            }}
          >
            <Icon name="close-outline" style={{ fontSize: 24 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Icon name="sync-outline" style={{ fontSize: 32, color: C.accent, animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 12, color: C.textMid }}>Fetching latest tax rates...</p>
            </div>
          ) : preview ? (
            <>
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
                <div style={{
                  padding: 16,
                  borderRadius: 10,
                  background: 'rgb(248, 250, 252)',
                  border: `1px solid ${C.border}`
                }}>
                  <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Added Rates</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'rgb(22, 163, 74)' }}>
                    {preview.summary.added || 0}
                  </div>
                </div>
                <div style={{
                  padding: 16,
                  borderRadius: 10,
                  background: 'rgb(248, 250, 252)',
                  border: `1px solid ${C.border}`
                }}>
                  <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Updated Rates</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'rgb(2, 132, 199)' }}>
                    {preview.summary.updated || 0}
                  </div>
                </div>
                <div style={{
                  padding: 16,
                  borderRadius: 10,
                  background: 'rgb(248, 250, 252)',
                  border: `1px solid ${C.border}`
                }}>
                  <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Skipped (No Data)</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'rgb(100, 116, 139)' }}>
                    {preview.summary.skipped || 0}
                  </div>
                </div>
                <div style={{
                  padding: 16,
                  borderRadius: 10,
                  background: 'rgb(248, 250, 252)',
                  border: `1px solid ${C.border}`
                }}>
                  <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>States Affected</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: C.text }}>
                    {preview.summary.statesAffected || 0}
                  </div>
                </div>
              </div>

              {/* Warning banner for skipped states */}
              {preview.summary.skipped > 0 && (
                <div style={{
                  padding: 14,
                  borderRadius: 10,
                  background: 'rgb(254, 243, 199)',
                  border: '1px solid rgb(217, 119, 6)',
                  marginBottom: 16,
                  display: 'flex',
                  gap: 10
                }}>
                  <Icon name="warning" style={{ fontSize: 20, color: 'rgb(217, 119, 6)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(120, 53, 15)', marginBottom: 4 }}>
                      {preview.summary.skipped} State{preview.summary.skipped > 1 ? 's' : ''} Skipped
                    </div>
                    <div style={{ fontSize: 12, color: 'rgb(120, 53, 15)', lineHeight: 1.4 }}>
                      The API did not return data for {preview.summary.skipped} state{preview.summary.skipped > 1 ? 's' : ''}. 
                      These states will remain unchanged in your config. Filter by "Skipped" to see which ones.
                    </div>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['all', 'added', 'updated', 'skipped'].map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: `1px solid ${filter === f ? C.accent : C.border}`,
                      background: filter === f ? 'rgb(204, 251, 241)' : 'white',
                      color: filter === f ? C.accent : C.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      textTransform: 'capitalize'
                    }}
                  >
                    {f} ({
                      f === 'all' ? preview.changes.length :
                      preview.changes.filter(c => c.changeType === f).length
                    })
                  </button>
                ))}
              </div>

              {/* Diff Table */}
              {filteredChanges.length > 0 ? (
                <div style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{
                        background: 'rgb(248, 250, 252)',
                        borderBottom: `1px solid ${C.border}`
                      }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Jurisdiction</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tax Type</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Effective Date</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Change</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChanges.map((change, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderBottom: idx < filteredChanges.length - 1 ? `1px solid ${C.border}` : 'none'
                          }}
                        >
                          <td style={{ padding: '10px 12px', fontWeight: 600 }}>{change.jurisdiction}</td>
                          <td style={{ padding: '10px 12px' }}>{change.taxType}</td>
                          <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{change.effectiveDate}</td>
                          <td style={{ padding: '10px 12px' }}>{changeTypeBadge(change.changeType)}</td>
                          <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: C.textMid }}>
                            {change.delta || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{
                  padding: 40,
                  textAlign: 'center',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  background: 'rgb(248, 250, 252)'
                }}>
                  <Icon name="checkmark-circle" style={{ fontSize: 48, color: 'rgb(22, 163, 74)' }} />
                  <h3 style={{ marginTop: 12, fontSize: 16, color: C.text }}>No changes detected</h3>
                  <p style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>
                    Your tax rates are already up to date.
                  </p>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        {preview && filteredChanges.length > 0 && (
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${C.border}`,
            background: 'rgb(248, 250, 252)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              I have reviewed these changes and understand they will affect tax calculations
            </label>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                data-testid="tax-sync-cancel-button"
                onClick={onClose}
                disabled={applying}
                style={{
                  ...S.buttonSecondary,
                  opacity: applying ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                data-testid="tax-sync-apply-button"
                onClick={handleApply}
                disabled={!confirmed || applying}
                style={{
                  ...S.buttonPrimary,
                  opacity: (!confirmed || applying) ? 0.6 : 1
                }}
              >
                {applying ? 'Applying...' : 'Apply Updates'}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
